import {
  Component,
  AfterViewInit,
  ElementRef,
  ViewChild,
  PLATFORM_ID,
  Inject,
  OnDestroy,
  signal,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { isPlatformBrowser } from '@angular/common';
import lightGallery from 'lightgallery';
import type { LightGallery } from 'lightgallery/lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';

// Interface defining the structure of a gallery image with before/after category
interface GalleryImage {
  preview: string; // Thumbnail preview path
  full: string; // Full-size image path
  alt: string; // Accessibility alt text
  project: string; // Project name
  category: 'before' | 'after' | 'none'; // Image category for badge display
}

// НОВЫЙ ТИП: добавлена категория 'finishing'
type ProjectCategory = 'all' | 'finishing' | 'installations' | 'sanding' | 'preparation';

// Interface defining the structure of a project card
interface ProjectCard {
  image: string; // Path to project image
  title: string; // Project title
  subtitle: string; // Project subtitle/description
  location: string; // Project location
  date: string; // Project completion date
  projectKey: string; // Unique project identifier to match gallery images
  category: ProjectCategory; // Project category for tab filtering
}

@Component({
  selector: 'app-slider-updated',
  imports: [CommonModule],
  templateUrl: './slider-updated.html',
  styleUrl: './slider-updated.scss',
})
export class SliderUpdated implements AfterViewInit, OnDestroy {
  @ViewChild('galleryContainer', { static: false }) galleryContainer?: ElementRef;

  isBrowser = false; // Flag to check if running in browser environment
  private lightGalleryInstance?: LightGallery; // LightGallery instance reference

  // Signal to track the currently active tab - по умолчанию теперь finishing
  activeTab = signal<ProjectCategory>('finishing');

  // Array of all project cards to display in the grid
  private allProjectCards: ProjectCard[] = [
    // --- SANDING (Остаются 6 самых старых) ---
    {
      image: '/project-slider/Atarmon/after/Artarmon9.webp',
      title: 'Artarmon Project',
      subtitle: 'Floor sanding, nail punching, hole filling and varnishing in Artarmon, Sydney.',
      location: 'Artarmon',
      date: 'Aug 2022',
      projectKey: 'Atarmon',
      category: 'sanding',
    },
    {
      image: '/project-slider/Chatswood1/after/1.webp',
      title: 'Chatswood Residence',
      subtitle: 'Floor and stair sanding, nail punching, hole filling and varnishing in Chatswood, Sydney.',
      location: 'Chatswood',
      date: 'Aug 2022',
      projectKey: 'Chatswood',
      category: 'sanding',
    },
    {
      image: '/project-slider/Chatswood2/after/Chatswood2.9.webp',
      title: 'Chatswood Complex',
      subtitle: 'Floor sanding, nail punching, hole filling and varnishing in Chatswood, Sydney.',
      location: 'Chatswood',
      date: 'Aug 2022',
      projectKey: 'Chatswood Two',
      category: 'sanding',
    },
    {
      image: '/project-slider/Randwick/after/Randwick7.webp',
      title: 'Randwick Renovation',
      subtitle: 'Timber floor sanding and varnishing in Randwick, Sydney.',
      location: 'Randwick',
      date: 'Aug 2022',
      projectKey: 'Randwick',
      category: 'sanding',
    },
    {
      image: '/project-slider/Turramurra/after/Turramurra6.webp',
      title: 'Turramurra Estate',
      subtitle: 'Parquet floor sanding, gap filling and varnishing in Turramurra, Sydney.',
      location: 'Turramurra',
      date: 'Aug 2022',
      projectKey: 'Turramurra',
      category: 'sanding',
    },
    {
      image: '/project-slider/Kensington/after/IMG_1886.webp',
      title: 'Kensington Residence',
      subtitle: 'Complete timber floor sanding and premium varnishing in Kensington, Sydney.',
      location: 'Kensington',
      date: 'Jun 2026',
      projectKey: 'Kensington',
      category: 'sanding',
    },

    // --- FINISHING (Переносим 5 новых проектов) ---
    {
      image: '/project-slider/North-Epping/after/IMG_1153.webp',
      title: 'North Epping Project',
      subtitle: 'Professional floor sanding, restoration and varnishing in North Epping.',
      location: 'North Epping',
      date: 'Jun 2026',
      projectKey: 'North-Epping',
      category: 'finishing',
    },
    {
      image: '/project-slider/Wilberforce/after/IMG_2064.webp',
      title: 'Wilberforce Project',
      subtitle: 'Complete timber floor sanding, restoration and premium varnishing in Wilberforce.',
      location: 'Wilberforce',
      date: 'Jun 2026',
      projectKey: 'Wilberforce',
      category: 'finishing',
    },
    {
      image: '/project-slider/Glenorie/after/Photo1.webp',
      title: 'Glenorie Project',
      subtitle: 'Floor sanding and varnishing in Glenorie.',
      location: 'Glenorie',
      date: 'Jul 2026',
      projectKey: 'Glenorie',
      category: 'finishing',
    },
    {
      image: '/project-slider/French-Forest/after/IMG_3560.webp',
      title: 'Project French Forest',
      subtitle: 'Floor sanding and varnishing in French Forest, Sydney.',
      location: 'French Forest',
      date: 'Aug 2026',
      projectKey: 'French-Forest',
      category: 'finishing',
    },
    {
      image: '/project-slider/Dior-Store/after/IMG_3650.webp',
      title: 'Dior Store',
      subtitle: 'Floor Sanding & Finishing – Parquet & Stairs in Dior Store, Sydney.',
      location: 'Sydney',
      date: 'Aug 2026',
      projectKey: 'Dior-Store',
      category: 'finishing',
    },

    // --- OTHER CATEGORIES ---
    {
      image: '/project-slider/Installments/23.webp',
      title: 'Professional Installments',
      subtitle: 'Timber floor installation, board laying and finishing in Sydney.',
      location: 'Sydney',
      date: 'Dec 2024',
      projectKey: 'Installments',
      category: 'installations',
    },
    {
      image: '/project-slider/Pre-installments/1.webp',
      title: 'Preparation Services',
      subtitle: 'Subfloor preparation, leveling and moisture barrier installation in Sydney.',
      location: 'Sydney',
      date: 'Dec 2024',
      projectKey: 'Pre-installments',
      category: 'preparation',
    },
  ];

  // Computed signal to filter projects based on active tab
  projectCards = computed(() => {
    const tab = this.activeTab();
    if (tab === 'all') {
      return this.allProjectCards;
    }
    return this.allProjectCards.filter((project) => project.category === tab);
  });

  // Complete gallery of all project images with before/after categorization
  private allGalleryImages: GalleryImage[] = [
    // Atarmon - After
    {
      preview: '/project-slider/Atarmon/after/Artarmon9.webp',
      full: '/project-slider/Atarmon/after/Artarmon9.webp',
      alt: 'Atarmon flooring project - after renovation view 2',
      project: 'Atarmon',
      category: 'after',
    },
    {
      preview: '/project-slider/Atarmon/after/Artarmon10.webp',
      full: '/project-slider/Atarmon/after/Artarmon10.webp',
      alt: 'Atarmon flooring project - after renovation view 3',
      project: 'Atarmon',
      category: 'after',
    },
    {
      preview: '/project-slider/Atarmon/after/Artarmon11.webp',
      full: '/project-slider/Atarmon/after/Artarmon11.webp',
      alt: 'Atarmon flooring project - after renovation view 4',
      project: 'Atarmon',
      category: 'after',
    },
    {
      preview: '/project-slider/Atarmon/after/Artarmon12.webp',
      full: '/project-slider/Atarmon/after/Artarmon12.webp',
      alt: 'Atarmon flooring project - after renovation view 5',
      project: 'Atarmon',
      category: 'after',
    },
    {
      preview: '/project-slider/Atarmon/after/Artarmon13.webp',
      full: '/project-slider/Atarmon/after/Artarmon13.webp',
      alt: 'Atarmon flooring project - after renovation view 6',
      project: 'Atarmon',
      category: 'after',
    },
    // Atarmon - Before
    {
      preview: '/project-slider/Atarmon/before/Artarmon1.webp',
      full: '/project-slider/Atarmon/before/Artarmon1.webp',
      alt: 'Atarmon flooring project - before renovation view 1',
      project: 'Atarmon',
      category: 'before',
    },
    {
      preview: '/project-slider/Atarmon/before/Artarmon2.webp',
      full: '/project-slider/Atarmon/before/Artarmon2.webp',
      alt: 'Atarmon flooring project - before renovation view 2',
      project: 'Atarmon',
      category: 'before',
    },
    {
      preview: '/project-slider/Atarmon/before/Artarmon3.webp',
      full: '/project-slider/Atarmon/before/Artarmon3.webp',
      alt: 'Atarmon flooring project - before renovation view 3',
      project: 'Atarmon',
      category: 'before',
    },
    {
      preview: '/project-slider/Atarmon/before/Artarmon4.webp',
      full: '/project-slider/Atarmon/before/Artarmon4.webp',
      alt: 'Atarmon flooring project - before renovation view 4',
      project: 'Atarmon',
      category: 'before',
    },
    {
      preview: '/project-slider/Atarmon/before/Artarmon5.webp',
      full: '/project-slider/Atarmon/before/Artarmon5.webp',
      alt: 'Atarmon flooring project - before renovation view 5',
      project: 'Atarmon',
      category: 'before',
    },
    {
      preview: '/project-slider/Atarmon/before/Artarmon6.webp',
      full: '/project-slider/Atarmon/before/Artarmon6.webp',
      alt: 'Atarmon flooring project - before renovation view 6',
      project: 'Atarmon',
      category: 'before',
    },
    {
      preview: '/project-slider/Atarmon/before/Artarmon7.webp',
      full: '/project-slider/Atarmon/before/Artarmon7.webp',
      alt: 'Atarmon flooring project - before renovation view 7',
      project: 'Atarmon',
      category: 'before',
    },
    // Chatswood1 - After
    {
      preview: '/project-slider/Chatswood1/after/1.webp',
      full: '/project-slider/Chatswood1/after/1.webp',
      alt: 'Chatswood flooring project - after renovation view 1',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/2.webp',
      full: '/project-slider/Chatswood1/after/2.webp',
      alt: 'Chatswood flooring project - after renovation view 2',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/3.webp',
      full: '/project-slider/Chatswood1/after/3.webp',
      alt: 'Chatswood flooring project - after renovation view 3',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/4.webp',
      full: '/project-slider/Chatswood1/after/4.webp',
      alt: 'Chatswood flooring project - after renovation view 4',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/5.webp',
      full: '/project-slider/Chatswood1/after/5.webp',
      alt: 'Chatswood flooring project - after renovation view 5',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/6.webp',
      full: '/project-slider/Chatswood1/after/6.webp',
      alt: 'Chatswood flooring project - after renovation view 6',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/7.webp',
      full: '/project-slider/Chatswood1/after/7.webp',
      alt: 'Chatswood flooring project - after renovation view 7',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/8.webp',
      full: '/project-slider/Chatswood1/after/8.webp',
      alt: 'Chatswood flooring project - after renovation view 8',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/9.webp',
      full: '/project-slider/Chatswood1/after/9.webp',
      alt: 'Chatswood flooring project - after renovation view 9',
      project: 'Chatswood',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood1/after/10.webp',
      full: '/project-slider/Chatswood1/after/10.webp',
      alt: 'Chatswood flooring project - after renovation view 10',
      project: 'Chatswood',
      category: 'after',
    },
    // Chatswood1 - Before
    {
      preview: '/project-slider/Chatswood1/before/1.webp',
      full: '/project-slider/Chatswood1/before/1.webp',
      alt: 'Chatswood flooring project - before renovation view 1',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/2.webp',
      full: '/project-slider/Chatswood1/before/2.webp',
      alt: 'Chatswood flooring project - before renovation view 2',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/3.webp',
      full: '/project-slider/Chatswood1/before/3.webp',
      alt: 'Chatswood flooring project - before renovation view 3',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/4.webp',
      full: '/project-slider/Chatswood1/before/4.webp',
      alt: 'Chatswood flooring project - before renovation view 4',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/5.webp',
      full: '/project-slider/Chatswood1/before/5.webp',
      alt: 'Chatswood flooring project - before renovation view 5',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/6.webp',
      full: '/project-slider/Chatswood1/before/6.webp',
      alt: 'Chatswood flooring project - before renovation view 6',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/7.webp',
      full: '/project-slider/Chatswood1/before/7.webp',
      alt: 'Chatswood flooring project - before renovation view 7',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/8.webp',
      full: '/project-slider/Chatswood1/before/8.webp',
      alt: 'Chatswood flooring project - before renovation view 8',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/9.webp',
      full: '/project-slider/Chatswood1/before/9.webp',
      alt: 'Chatswood flooring project - before renovation view 9',
      project: 'Chatswood',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood1/before/10.webp',
      full: '/project-slider/Chatswood1/before/10.webp',
      alt: 'Chatswood flooring project - before renovation view 10',
      project: 'Chatswood',
      category: 'before',
    },
    // Chatswood2 - After
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.9.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.9.webp',
      alt: 'Chatswood Two flooring project - after renovation view 1',
      project: 'Chatswood Two',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.10.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.10.webp',
      alt: 'Chatswood Two flooring project - after renovation view 2',
      project: 'Chatswood Two',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.11.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.11.webp',
      alt: 'Chatswood Two flooring project - after renovation view 3',
      project: 'Chatswood Two',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.12.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.12.webp',
      alt: 'Chatswood Two flooring project - after renovation view 4',
      project: 'Chatswood Two',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.13.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.13.webp',
      alt: 'Chatswood Two flooring project - after renovation view 5',
      project: 'Chatswood Two',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.14.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.14.webp',
      alt: 'Chatswood Two flooring project - after renovation view 6',
      project: 'Chatswood Two',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.15.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.15.webp',
      alt: 'Chatswood Two flooring project - after renovation view 7',
      project: 'Chatswood Two',
      category: 'after',
    },
    {
      preview: '/project-slider/Chatswood2/after/Chatswood2.16.webp',
      full: '/project-slider/Chatswood2/after/Chatswood2.16.webp',
      alt: 'Chatswood Two flooring project - after renovation view 8',
      project: 'Chatswood Two',
      category: 'after',
    },
    // Chatswood2 - Before
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.1.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.1.webp',
      alt: 'Chatswood Two flooring project - before renovation view 1',
      project: 'Chatswood Two',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.2.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.2.webp',
      alt: 'Chatswood Two flooring project - before renovation view 2',
      project: 'Chatswood Two',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.3.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.3.webp',
      alt: 'Chatswood Two flooring project - before renovation view 3',
      project: 'Chatswood Two',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.4.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.4.webp',
      alt: 'Chatswood Two flooring project - before renovation view 4',
      project: 'Chatswood Two',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.5.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.5.webp',
      alt: 'Chatswood Two flooring project - before renovation view 5',
      project: 'Chatswood Two',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.6.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.6.webp',
      alt: 'Chatswood Two flooring project - before renovation view 6',
      project: 'Chatswood Two',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.7.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.7.webp',
      alt: 'Chatswood Two flooring project - before renovation view 7',
      project: 'Chatswood Two',
      category: 'before',
    },
    {
      preview: '/project-slider/Chatswood2/before/Chatswood2.8.webp',
      full: '/project-slider/Chatswood2/before/Chatswood2.8.webp',
      alt: 'Chatswood Two flooring project - before renovation view 8',
      project: 'Chatswood Two',
      category: 'before',
    },
    // Randwick - After
    {
      preview: '/project-slider/Randwick/after/Randwick7.webp',
      full: '/project-slider/Randwick/after/Randwick7.webp',
      alt: 'Randwick flooring project - after renovation view 1',
      project: 'Randwick',
      category: 'after',
    },
    {
      preview: '/project-slider/Randwick/after/Randwick8.webp',
      full: '/project-slider/Randwick/after/Randwick8.webp',
      alt: 'Randwick flooring project - after renovation view 2',
      project: 'Randwick',
      category: 'after',
    },
    {
      preview: '/project-slider/Randwick/after/Randwick9.webp',
      full: '/project-slider/Randwick/after/Randwick9.webp',
      alt: 'Randwick flooring project - after renovation view 3',
      project: 'Randwick',
      category: 'after',
    },
    // Randwick - Before
    {
      preview: '/project-slider/Randwick/before/Randwick1.webp',
      full: '/project-slider/Randwick/before/Randwick1.webp',
      alt: 'Randwick flooring project - before renovation view 1',
      project: 'Randwick',
      category: 'before',
    },
    {
      preview: '/project-slider/Randwick/before/Randwick2.webp',
      full: '/project-slider/Randwick/before/Randwick2.webp',
      alt: 'Randwick flooring project - before renovation view 2',
      project: 'Randwick',
      category: 'before',
    },
    {
      preview: '/project-slider/Randwick/before/Randwick3.webp',
      full: '/project-slider/Randwick/before/Randwick3.webp',
      alt: 'Randwick flooring project - before renovation view 3',
      project: 'Randwick',
      category: 'before',
    },
    {
      preview: '/project-slider/Randwick/before/Randwick4.webp',
      full: '/project-slider/Randwick/before/Randwick4.webp',
      alt: 'Randwick flooring project - before renovation view 4',
      project: 'Randwick',
      category: 'before',
    },
    {
      preview: '/project-slider/Randwick/before/Randwick5.webp',
      full: '/project-slider/Randwick/before/Randwick5.webp',
      alt: 'Randwick flooring project - before renovation view 5',
      project: 'Randwick',
      category: 'before',
    },
    {
      preview: '/project-slider/Randwick/before/Randwick6.webp',
      full: '/project-slider/Randwick/before/Randwick6.webp',
      alt: 'Randwick flooring project - before renovation view 6',
      project: 'Randwick',
      category: 'before',
    },
    // Turramurra - After
    {
      preview: '/project-slider/Turramurra/after/Turramurra6.webp',
      full: '/project-slider/Turramurra/after/Turramurra6.webp',
      alt: 'Turramurra flooring project - after renovation view 1',
      project: 'Turramurra',
      category: 'after',
    },
    {
      preview: '/project-slider/Turramurra/after/Turramurra7.webp',
      full: '/project-slider/Turramurra/after/Turramurra7.webp',
      alt: 'Turramurra flooring project - after renovation view 2',
      project: 'Turramurra',
      category: 'after',
    },
    {
      preview: '/project-slider/Turramurra/after/Turramurra8.webp',
      full: '/project-slider/Turramurra/after/Turramurra8.webp',
      alt: 'Turramurra flooring project - after renovation view 3',
      project: 'Turramurra',
      category: 'after',
    },
    {
      preview: '/project-slider/Turramurra/after/Turramurra9.webp',
      full: '/project-slider/Turramurra/after/Turramurra9.webp',
      alt: 'Turramurra flooring project - after renovation view 4',
      project: 'Turramurra',
      category: 'after',
    },
    {
      preview: '/project-slider/Turramurra/after/Turramurra10.webp',
      full: '/project-slider/Turramurra/after/Turramurra10.webp',
      alt: 'Turramurra flooring project - after renovation view 5',
      project: 'Turramurra',
      category: 'after',
    },
    {
      preview: '/project-slider/Turramurra/after/Turramurra11.webp',
      full: '/project-slider/Turramurra/after/Turramurra11.webp',
      alt: 'Turramurra flooring project - after renovation view 6',
      project: 'Turramurra',
      category: 'after',
    },
    // Turramurra - Before
    {
      preview: '/project-slider/Turramurra/before/Turramurra1.webp',
      full: '/project-slider/Turramurra/before/Turramurra1.webp',
      alt: 'Turramurra flooring project - before renovation view 1',
      project: 'Turramurra',
      category: 'before',
    },
    {
      preview: '/project-slider/Turramurra/before/Turramurra2.webp',
      full: '/project-slider/Turramurra/before/Turramurra2.webp',
      alt: 'Turramurra flooring project - before renovation view 2',
      project: 'Turramurra',
      category: 'before',
    },
    {
      preview: '/project-slider/Turramurra/before/Turramurra3.webp',
      full: '/project-slider/Turramurra/before/Turramurra3.webp',
      alt: 'Turramurra flooring project - before renovation view 3',
      project: 'Turramurra',
      category: 'before',
    },
    {
      preview: '/project-slider/Turramurra/before/Turramurra4.webp',
      full: '/project-slider/Turramurra/before/Turramurra4.webp',
      alt: 'Turramurra flooring project - before renovation view 4',
      project: 'Turramurra',
      category: 'before',
    },
    {
      preview: '/project-slider/Turramurra/before/Turramurra5.webp',
      full: '/project-slider/Turramurra/before/Turramurra5.webp',
      alt: 'Turramurra flooring project - before renovation view 5',
      project: 'Turramurra',
      category: 'before',
    },
    // Installments
    {
      preview: '/project-slider/Installments/1.webp',
      full: '/project-slider/Installments/1.webp',
      alt: 'Installments flooring project - professional installation view 1',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/2.webp',
      full: '/project-slider/Installments/2.webp',
      alt: 'Installments flooring project - professional installation view 2',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/3.webp',
      full: '/project-slider/Installments/3.webp',
      alt: 'Installments flooring project - professional installation view 3',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/4.webp',
      full: '/project-slider/Installments/4.webp',
      alt: 'Installments flooring project - professional installation view 4',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/5.webp',
      full: '/project-slider/Installments/5.webp',
      alt: 'Installments flooring project - professional installation view 5',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/6.webp',
      full: '/project-slider/Installments/6.webp',
      alt: 'Installments flooring project - professional installation view 6',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/7.webp',
      full: '/project-slider/Installments/7.webp',
      alt: 'Installments flooring project - professional installation view 7',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/8.webp',
      full: '/project-slider/Installments/8.webp',
      alt: 'Installments flooring project - professional installation view 8',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/9.webp',
      full: '/project-slider/Installments/9.webp',
      alt: 'Installments flooring project - professional installation view 9',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/10.webp',
      full: '/project-slider/Installments/10.webp',
      alt: 'Installments flooring project - professional installation view 10',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/11.webp',
      full: '/project-slider/Installments/11.webp',
      alt: 'Installments flooring project - professional installation view 11',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/12.webp',
      full: '/project-slider/Installments/12.webp',
      alt: 'Installments flooring project - professional installation view 12',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/13.webp',
      full: '/project-slider/Installments/13.webp',
      alt: 'Installments flooring project - professional installation view 13',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/14.webp',
      full: '/project-slider/Installments/14.webp',
      alt: 'Installments flooring project - professional installation view 14',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/15.webp',
      full: '/project-slider/Installments/15.webp',
      alt: 'Installments flooring project - professional installation view 15',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/16.webp',
      full: '/project-slider/Installments/16.webp',
      alt: 'Installments flooring project - professional installation view 16',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/17.webp',
      full: '/project-slider/Installments/17.webp',
      alt: 'Installments flooring project - professional installation view 17',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/18.webp',
      full: '/project-slider/Installments/18.webp',
      alt: 'Installments flooring project - professional installation view 18',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/19.webp',
      full: '/project-slider/Installments/19.webp',
      alt: 'Installments flooring project - professional installation view 19',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/20.webp',
      full: '/project-slider/Installments/20.webp',
      alt: 'Installments flooring project - professional installation view 20',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/21.webp',
      full: '/project-slider/Installments/21.webp',
      alt: 'Installments flooring project - professional installation view 21',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/22.webp',
      full: '/project-slider/Installments/22.webp',
      alt: 'Installments flooring project - professional installation view 22',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/23.webp',
      full: '/project-slider/Installments/23.webp',
      alt: 'Installments flooring project - professional installation view 23',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/24.webp',
      full: '/project-slider/Installments/24.webp',
      alt: 'Installments flooring project - professional installation view 24',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/25.webp',
      full: '/project-slider/Installments/25.webp',
      alt: 'Installments flooring project - professional installation view 25',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/26.webp',
      full: '/project-slider/Installments/26.webp',
      alt: 'Installments flooring project - professional installation view 26',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/27.webp',
      full: '/project-slider/Installments/27.webp',
      alt: 'Installments flooring project - professional installation view 27',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/28.webp',
      full: '/project-slider/Installments/28.webp',
      alt: 'Installments flooring project - professional installation view 28',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/29.webp',
      full: '/project-slider/Installments/29.webp',
      alt: 'Installments flooring project - professional installation view 29',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/30.webp',
      full: '/project-slider/Installments/30.webp',
      alt: 'Installments flooring project - professional installation view 30',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/31.webp',
      full: '/project-slider/Installments/31.webp',
      alt: 'Installments flooring project - professional installation view 31',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/32.webp',
      full: '/project-slider/Installments/32.webp',
      alt: 'Installments flooring project - professional installation view 32',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/33.webp',
      full: '/project-slider/Installments/33.webp',
      alt: 'Installments flooring project - professional installation view 33',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/34.webp',
      full: '/project-slider/Installments/34.webp',
      alt: 'Installments flooring project - professional installation view 34',
      project: 'Installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Installments/35.webp',
      full: '/project-slider/Installments/35.webp',
      alt: 'Installments flooring project - professional installation view 35',
      project: 'Installments',
      category: 'none',
    },
    // Pre-installments
    {
      preview: '/project-slider/Pre-installments/1.webp',
      full: '/project-slider/Pre-installments/1.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 1',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/2.webp',
      full: '/project-slider/Pre-installments/2.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 2',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/3.webp',
      full: '/project-slider/Pre-installments/3.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 3',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/4.webp',
      full: '/project-slider/Pre-installments/4.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 4',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/5.webp',
      full: '/project-slider/Pre-installments/5.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 5',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/6.webp',
      full: '/project-slider/Pre-installments/6.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 6',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/7.webp',
      full: '/project-slider/Pre-installments/7.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 7',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/8.webp',
      full: '/project-slider/Pre-installments/8.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 8',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/9.webp',
      full: '/project-slider/Pre-installments/9.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 9',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/10.webp',
      full: '/project-slider/Pre-installments/10.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 10',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/11.webp',
      full: '/project-slider/Pre-installments/11.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 11',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/12.webp',
      full: '/project-slider/Pre-installments/12.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 12',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/13.webp',
      full: '/project-slider/Pre-installments/13.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 13',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/14.webp',
      full: '/project-slider/Pre-installments/14.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 14',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/15.webp',
      full: '/project-slider/Pre-installments/15.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 15',
      project: 'Pre-installments',
      category: 'none',
    },
    {
      preview: '/project-slider/Pre-installments/16.webp',
      full: '/project-slider/Pre-installments/16.webp',
      alt: 'Pre-installments flooring project - professional Preparation service view 16',
      project: 'Pre-installments',
      category: 'none',
    },
    // --- KENSINGTON PROJECT ---
    // Kensington - After
    {
      preview: '/project-slider/Kensington/after/IMG_1886.webp',
      full: '/project-slider/Kensington/after/IMG_1886.webp',
      alt: 'Kensington flooring project - after sanding and varnishing view 1',
      project: 'Kensington',
      category: 'after',
    },
    {
      preview: '/project-slider/Kensington/after/IMG_1887.webp',
      full: '/project-slider/Kensington/after/IMG_1887.webp',
      alt: 'Kensington flooring project - after sanding and varnishing view 2',
      project: 'Kensington',
      category: 'after',
    },
    {
      preview: '/project-slider/Kensington/after/IMG_1888.webp',
      full: '/project-slider/Kensington/after/IMG_1888.webp',
      alt: 'Kensington flooring project - after sanding and varnishing view 3',
      project: 'Kensington',
      category: 'after',
    },
    // Kensington - Before
    {
      preview: '/project-slider/Kensington/before/IMG_1839.webp',
      full: '/project-slider/Kensington/before/IMG_1839.webp',
      alt: 'Kensington flooring project - before renovation view 1',
      project: 'Kensington',
      category: 'before',
    },
    {
      preview: '/project-slider/Kensington/before/IMG_1840.webp',
      full: '/project-slider/Kensington/before/IMG_1840.webp',
      alt: 'Kensington flooring project - before renovation view 2',
      project: 'Kensington',
      category: 'before',
    },
    {
      preview: '/project-slider/Kensington/before/IMG_1841.webp',
      full: '/project-slider/Kensington/before/IMG_1841.webp',
      alt: 'Kensington flooring project - before renovation view 3',
      project: 'Kensington',
      category: 'before',
    },
    {
      preview: '/project-slider/Kensington/before/IMG_1842.webp',
      full: '/project-slider/Kensington/before/IMG_1842.webp',
      alt: 'Kensington flooring project - before renovation view 4',
      project: 'Kensington',
      category: 'before',
    },
    {
      preview: '/project-slider/Kensington/before/IMG_1843.webp',
      full: '/project-slider/Kensington/before/IMG_1843.webp',
      alt: 'Kensington flooring project - before renovation view 5',
      project: 'Kensington',
      category: 'before',
    },

    // --- NORTH-EPPING PROJECT ---
    // North-Epping - After
    {
      preview: '/project-slider/North-Epping/after/IMG_1153.webp',
      full: '/project-slider/North-Epping/after/IMG_1153.webp',
      alt: 'North Epping flooring project - after sanding and varnishing view 1',
      project: 'North-Epping',
      category: 'after',
    },
    {
      preview: '/project-slider/North-Epping/after/IMG_1154.webp',
      full: '/project-slider/North-Epping/after/IMG_1154.webp',
      alt: 'North Epping flooring project - after sanding and varnishing view 2',
      project: 'North-Epping',
      category: 'after',
    },
    {
      preview: '/project-slider/North-Epping/after/IMG_1155.webp',
      full: '/project-slider/North-Epping/after/IMG_1155.webp',
      alt: 'North Epping flooring project - after sanding and varnishing view 3',
      project: 'North-Epping',
      category: 'after',
    },
    // North-Epping - Before
    {
      preview: '/project-slider/North-Epping/before/IMG_1060.webp',
      full: '/project-slider/North-Epping/before/IMG_1060.webp',
      alt: 'North Epping flooring project - before renovation view 1',
      project: 'North-Epping',
      category: 'before',
    },
    {
      preview: '/project-slider/North-Epping/before/IMG_1061.webp',
      full: '/project-slider/North-Epping/before/IMG_1061.webp',
      alt: 'North Epping flooring project - before renovation view 2',
      project: 'North-Epping',
      category: 'before',
    },
    {
      preview: '/project-slider/North-Epping/before/IMG_1062.webp',
      full: '/project-slider/North-Epping/before/IMG_1062.webp',
      alt: 'North Epping flooring project - before renovation view 3',
      project: 'North-Epping',
      category: 'before',
    },
    {
      preview: '/project-slider/North-Epping/before/IMG_1063.webp',
      full: '/project-slider/North-Epping/before/IMG_1063.webp',
      alt: 'North Epping flooring project - before renovation view 4',
      project: 'North-Epping',
      category: 'before',
    },

    // --- WARRAWEE PROJECT ---
    // Warrawee - After
    {
      preview: '/project-slider/Warrawee/after/IMG_1568.webp',
      full: '/project-slider/Warrawee/after/IMG_1568.webp',
      alt: 'Warrawee flooring project - after installation view 1',
      project: 'Warrawee',
      category: 'after',
    },
    {
      preview: '/project-slider/Warrawee/after/IMG_1569.webp',
      full: '/project-slider/Warrawee/after/IMG_1569.webp',
      alt: 'Warrawee flooring project - after installation view 2',
      project: 'Warrawee',
      category: 'after',
    },
    {
      preview: '/project-slider/Warrawee/after/IMG_1570.webp',
      full: '/project-slider/Warrawee/after/IMG_1570.webp',
      alt: 'Warrawee flooring project - after installation view 3',
      project: 'Warrawee',
      category: 'after',
    },
    {
      preview: '/project-slider/Warrawee/after/IMG_1573.webp',
      full: '/project-slider/Warrawee/after/IMG_1573.webp',
      alt: 'Warrawee flooring project - after installation view 4',
      project: 'Warrawee',
      category: 'after',
    },
    {
      preview: '/project-slider/Warrawee/after/IMG_1579.webp',
      full: '/project-slider/Warrawee/after/IMG_1579.webp',
      alt: 'Warrawee flooring project - after installation view 5',
      project: 'Warrawee',
      category: 'after',
    },
    {
      preview: '/project-slider/Warrawee/after/IMG_1583.webp',
      full: '/project-slider/Warrawee/after/IMG_1583.webp',
      alt: 'Warrawee flooring project - after installation view 6',
      project: 'Warrawee',
      category: 'after',
    },
    // Warrawee - Before
    {
      preview: '/project-slider/Warrawee/before/IMG_1252.webp',
      full: '/project-slider/Warrawee/before/IMG_1252.webp',
      alt: 'Warrawee flooring project - before installation view 1',
      project: 'Warrawee',
      category: 'before',
    },
    {
      preview: '/project-slider/Warrawee/before/IMG_1255.webp',
      full: '/project-slider/Warrawee/before/IMG_1255.webp',
      alt: 'Warrawee flooring project - before installation view 2',
      project: 'Warrawee',
      category: 'before',
    },
    {
      preview: '/project-slider/Warrawee/before/IMG_1256.webp',
      full: '/project-slider/Warrawee/before/IMG_1256.webp',
      alt: 'Warrawee flooring project - before installation view 3',
      project: 'Warrawee',
      category: 'before',
    },
    
    // --- WILBERFORCE PROJECT ---
    // Wilberforce - After
    {
      preview: '/project-slider/Wilberforce/after/IMG_2064.webp',
      full: '/project-slider/Wilberforce/after/IMG_2064.webp',
      alt: 'Wilberforce flooring project - after sanding and varnishing view 1',
      project: 'Wilberforce',
      category: 'after',
    },
    {
      preview: '/project-slider/Wilberforce/after/IMG_2065.webp',
      full: '/project-slider/Wilberforce/after/IMG_2065.webp',
      alt: 'Wilberforce flooring project - after sanding and varnishing view 2',
      project: 'Wilberforce',
      category: 'after',
    },
    {
      preview: '/project-slider/Wilberforce/after/IMG_2067.webp',
      full: '/project-slider/Wilberforce/after/IMG_2067.webp',
      alt: 'Wilberforce flooring project - after sanding and varnishing view 3',
      project: 'Wilberforce',
      category: 'after',
    },
    {
      preview: '/project-slider/Wilberforce/after/IMG_2068.webp',
      full: '/project-slider/Wilberforce/after/IMG_2068.webp',
      alt: 'Wilberforce flooring project - after sanding and varnishing view 4',
      project: 'Wilberforce',
      category: 'after',
    },
    {
      preview: '/project-slider/Wilberforce/after/IMG_2069.webp',
      full: '/project-slider/Wilberforce/after/IMG_2069.webp',
      alt: 'Wilberforce flooring project - after sanding and varnishing view 5',
      project: 'Wilberforce',
      category: 'after',
    },
    {
      preview: '/project-slider/Wilberforce/after/IMG_2070.webp',
      full: '/project-slider/Wilberforce/after/IMG_2070.webp',
      alt: 'Wilberforce flooring project - after sanding and varnishing view 6',
      project: 'Wilberforce',
      category: 'after',
    },
    // Wilberforce - Before
    {
      preview: '/project-slider/Wilberforce/before/IMG_2071.webp',
      full: '/project-slider/Wilberforce/before/IMG_2071.webp',
      alt: 'Wilberforce flooring project - before renovation view 1',
      project: 'Wilberforce',
      category: 'before',
    },
    {
      preview: '/project-slider/Wilberforce/before/IMG_2072.webp',
      full: '/project-slider/Wilberforce/before/IMG_2072.webp',
      alt: 'Wilberforce flooring project - before renovation view 2',
      project: 'Wilberforce',
      category: 'before',
    },
    {
      preview: '/project-slider/Wilberforce/before/IMG_2075.webp',
      full: '/project-slider/Wilberforce/before/IMG_2075.webp',
      alt: 'Wilberforce flooring project - before renovation view 3',
      project: 'Wilberforce',
      category: 'before',
    },

    // --- GLENORIE PROJECT ---
    // Glenorie - After
    {
      preview: '/project-slider/Glenorie/after/Photo1.webp',
      full: '/project-slider/Glenorie/after/Photo1.webp',
      alt: 'Glenorie flooring project - after sanding and varnishing view 1',
      project: 'Glenorie',
      category: 'after',
    },
    {
      preview: '/project-slider/Glenorie/after/Photo2.webp',
      full: '/project-slider/Glenorie/after/Photo2.webp',
      alt: 'Glenorie flooring project - after sanding and varnishing view 2',
      project: 'Glenorie',
      category: 'after',
    },
    {
      preview: '/project-slider/Glenorie/after/Photo3.webp',
      full: '/project-slider/Glenorie/after/Photo3.webp',
      alt: 'Glenorie flooring project - after sanding and varnishing view 3',
      project: 'Glenorie',
      category: 'after',
    },
    {
      preview: '/project-slider/Glenorie/after/Photo4.webp',
      full: '/project-slider/Glenorie/after/Photo4.webp',
      alt: 'Glenorie flooring project - after sanding and varnishing view 4',
      project: 'Glenorie',
      category: 'after',
    },
    {
      preview: '/project-slider/Glenorie/after/Photo5.webp',
      full: '/project-slider/Glenorie/after/Photo5.webp',
      alt: 'Glenorie flooring project - after sanding and varnishing view 5',
      project: 'Glenorie',
      category: 'after',
    },
    {
      preview: '/project-slider/Glenorie/after/Photo6.webp',
      full: '/project-slider/Glenorie/after/Photo6.webp',
      alt: 'Glenorie flooring project - after sanding and varnishing view 6',
      project: 'Glenorie',
      category: 'after',
    },
    {
      preview: '/project-slider/Glenorie/after/Photo7.webp',
      full: '/project-slider/Glenorie/after/Photo7.webp',
      alt: 'Glenorie flooring project - after sanding and varnishing view 7',
      project: 'Glenorie',
      category: 'after',
    },
    // Glenorie - Before
    {
      preview: '/project-slider/Glenorie/before/IMG_3020.webp',
      full: '/project-slider/Glenorie/before/IMG_3020.webp',
      alt: 'Glenorie flooring project - before renovation view 1',
      project: 'Glenorie',
      category: 'before',
    },
    {
      preview: '/project-slider/Glenorie/before/IMG_3021.webp',
      full: '/project-slider/Glenorie/before/IMG_3021.webp',
      alt: 'Glenorie flooring project - before renovation view 2',
      project: 'Glenorie',
      category: 'before',
    },
    {
      preview: '/project-slider/Glenorie/before/IMG_3022.webp',
      full: '/project-slider/Glenorie/before/IMG_3022.webp',
      alt: 'Glenorie flooring project - before renovation view 3',
      project: 'Glenorie',
      category: 'before',
    },
    {
      preview: '/project-slider/Glenorie/before/IMG_3024.webp',
      full: '/project-slider/Glenorie/before/IMG_3024.webp',
      alt: 'Glenorie flooring project - before renovation view 4',
      project: 'Glenorie',
      category: 'before',
    },

    // --- FRENCH FOREST PROJECT ---
    // French-Forest - After
    {
      preview: '/project-slider/French-Forest/after/IMG_3560.webp',
      full: '/project-slider/French-Forest/after/IMG_3560.webp',
      alt: 'Project French Forest flooring project - after sanding and varnishing view 1',
      project: 'French-Forest',
      category: 'after',
    },
    {
      preview: '/project-slider/French-Forest/after/IMG_3562.webp',
      full: '/project-slider/French-Forest/after/IMG_3562.webp',
      alt: 'Project French Forest flooring project - after sanding and varnishing view 2',
      project: 'French-Forest',
      category: 'after',
    },
    {
      preview: '/project-slider/French-Forest/after/IMG_3563.webp',
      full: '/project-slider/French-Forest/after/IMG_3563.webp',
      alt: 'Project French Forest flooring project - after sanding and varnishing view 3',
      project: 'French-Forest',
      category: 'after',
    },
    {
      preview: '/project-slider/French-Forest/after/IMG_3565.webp',
      full: '/project-slider/French-Forest/after/IMG_3565.webp',
      alt: 'Project French Forest flooring project - after sanding and varnishing view 4',
      project: 'French-Forest',
      category: 'after',
    },
    {
      preview: '/project-slider/French-Forest/after/IMG_3566.webp',
      full: '/project-slider/French-Forest/after/IMG_3566.webp',
      alt: 'Project French Forest flooring project - after sanding and varnishing view 5',
      project: 'French-Forest',
      category: 'after',
    },
    {
      preview: '/project-slider/French-Forest/after/IMG_3568.webp',
      full: '/project-slider/French-Forest/after/IMG_3568.webp',
      alt: 'Project French Forest flooring project - after sanding and varnishing view 6',
      project: 'French-Forest',
      category: 'after',
    },
    // French-Forest - Before
    {
      preview: '/project-slider/French-Forest/before/IMG_3481.webp',
      full: '/project-slider/French-Forest/before/IMG_3481.webp',
      alt: 'Project French Forest flooring project - before renovation view 1',
      project: 'French-Forest',
      category: 'before',
    },
    {
      preview: '/project-slider/French-Forest/before/IMG_3482.webp',
      full: '/project-slider/French-Forest/before/IMG_3482.webp',
      alt: 'Project French Forest flooring project - before renovation view 2',
      project: 'French-Forest',
      category: 'before',
    },
    {
      preview: '/project-slider/French-Forest/before/IMG_3491.webp',
      full: '/project-slider/French-Forest/before/IMG_3491.webp',
      alt: 'Project French Forest flooring project - before renovation view 3',
      project: 'French-Forest',
      category: 'before',
    },
    {
      preview: '/project-slider/French-Forest/before/IMG_3493.webp',
      full: '/project-slider/French-Forest/before/IMG_3493.webp',
      alt: 'Project French Forest flooring project - before renovation view 4',
      project: 'French-Forest',
      category: 'before',
    },

    // --- DIOR STORE PROJECT ---
    // Dior-Store - Images (No before photos)
    {
      preview: '/project-slider/Dior-Store/after/IMG_3650.webp',
      full: '/project-slider/Dior-Store/after/IMG_3650.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 1',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3651.webp',
      full: '/project-slider/Dior-Store/after/IMG_3651.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 2',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3652.webp',
      full: '/project-slider/Dior-Store/after/IMG_3652.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 3',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3653.webp',
      full: '/project-slider/Dior-Store/after/IMG_3653.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 4',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3654.webp',
      full: '/project-slider/Dior-Store/after/IMG_3654.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 5',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3655.webp',
      full: '/project-slider/Dior-Store/after/IMG_3655.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 6',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3656.webp',
      full: '/project-slider/Dior-Store/after/IMG_3656.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 7',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3657.webp',
      full: '/project-slider/Dior-Store/after/IMG_3657.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 8',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3658.webp',
      full: '/project-slider/Dior-Store/after/IMG_3658.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 9',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3659.webp',
      full: '/project-slider/Dior-Store/after/IMG_3659.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 10',
      project: 'Dior-Store',
      category: 'none',
    },
    {
      preview: '/project-slider/Dior-Store/after/IMG_3660.webp',
      full: '/project-slider/Dior-Store/after/IMG_3660.webp',
      alt: 'Dior Store flooring project - Floor Sanding & Finishing view 11',
      project: 'Dior-Store',
      category: 'none',
    },
  ];

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  // Method to change active tab and filter projects
  setActiveTab(category: ProjectCategory): void {
    this.activeTab.set(category);
  }

  // Method to get gallery images for a specific project
  getProjectImages(projectKey: string): GalleryImage[] {
    return this.allGalleryImages.filter((img) => img.project === projectKey);
  }

  // Method to handle arrow button click and open lightGallery for specific project
  openProjectGallery(project: ProjectCard, event: Event): void {
    event.preventDefault();
    event.stopPropagation();

    if (!this.isBrowser) {
      return;
    }

    // Get images for this project
    const projectImages = this.getProjectImages(project.projectKey);

    // Initialize lightGallery with these images
    this.initializeLightGallery(projectImages);
  }

  ngAfterViewInit(): void {}

  // Initialize lightGallery with specific project images
  private initializeLightGallery(images: GalleryImage[]): void {
    if (!this.isBrowser || !this.galleryContainer) {
      return;
    }

    this.destroyLightGallery();

    const galleryElement = this.galleryContainer.nativeElement;

    // Clear existing gallery items
    galleryElement.innerHTML = '';

    // Add gallery items dynamically
    images.forEach((image) => {
      const link = document.createElement('a');
      link.href = image.full;
      link.setAttribute('data-thumb', image.preview);
      link.setAttribute('data-category', image.category);
      link.setAttribute('aria-label', `View ${image.alt} in fullscreen`);
      link.style.display = 'none'; // Hide links, only used for lightGallery
      galleryElement.appendChild(link);
    });

    // Initialize lightGallery
    this.lightGalleryInstance = lightGallery(galleryElement, {
      plugins: [lgThumbnail],
      speed: 500,
      licenseKey: 'GPLv3',
      download: false,
      counter: true,
      thumbnail: true,
      animateThumb: true,
      allowMediaOverlap: true,
      thumbWidth: 100,
      thumbHeight: '80px',
      thumbMargin: 5,
      exThumbImage: 'data-thumb',
      toggleThumb: true,
      closable: true,
      escKey: true,
    });

    // Add event listeners for badge display - same as final-slider
    galleryElement.addEventListener('lgAfterOpen', () => {
      this.addCategoryBadge();
      this.resizeImages();
    });

    galleryElement.addEventListener('lgAfterSlide', () => {
      this.addCategoryBadge();
      this.resizeImages();
    });

    // Add event listener after image loads to force resize
    galleryElement.addEventListener('lgAfterAppendSlide', () => {
      this.resizeImages();
    });

    // Automatically open gallery after initialization
    if (this.lightGalleryInstance) {
      this.lightGalleryInstance.openGallery(0);
    }
  }

  // Add category badge (before/after) to current image in lightGallery
  private addCategoryBadge(): void {
    if (!this.lightGalleryInstance || !this.galleryContainer) {
      console.warn('lightGalleryInstance or galleryContainer not available');
      return;
    }

    // Remove existing badge if present
    const existingBadge = document.querySelector('.lg-category-badge');
    if (existingBadge) {
      existingBadge.remove();
    }

    const currentIndex = this.lightGalleryInstance.index;
    const galleryItems = this.galleryContainer.nativeElement.querySelectorAll('a');

    console.log('Current index:', currentIndex);
    console.log('Gallery items count:', galleryItems.length);

    if (currentIndex >= 0 && currentIndex < galleryItems.length) {
      const category = galleryItems[currentIndex].getAttribute('data-category');
      console.log('Category:', category);

      if (category && category !== 'none') {
        const badge = document.createElement('div');
        badge.className = 'lg-category-badge';
        badge.textContent = category === 'before' ? 'Before' : 'After';
        badge.setAttribute('data-category', category);

        // Calculate responsive font size based on screen width
        const screenWidth = window.innerWidth;
        let fontSize = 40; // Default for desktop
        let letterSpacing = 2;

        if (screenWidth <= 480) {
          fontSize = 20; // Mobile small
          letterSpacing = 1;
        } else if (screenWidth <= 768) {
          fontSize = 28; // Tablet
          letterSpacing = 1.5;
        } else if (screenWidth <= 1024) {
          fontSize = 35; // Small desktop
          letterSpacing = 1.8;
        }

        // Add inline styles with responsive values
        badge.style.cssText = `
          position: fixed !important;
          top: 70px !important;
          left: 50% !important;
          transform: translateX(-50%) !important;
          z-index: 10000 !important;
          font-size: ${fontSize}px !important;
          font-weight: 900 !important;
          color: #7a3d1f !important;
          text-transform: uppercase !important;
          letter-spacing: ${letterSpacing}px !important;
          pointer-events: none !important;
          text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5), 2px 2px 6px rgba(0, 0, 0, 0.3) !important;
          white-space: nowrap !important;
          font-family: 'Poppins', sans-serif !important;
        `;

        // Append badge to body instead of slide to ensure visibility
        const lgOuter = document.querySelector('.lg-outer');
        console.log('LG outer found:', !!lgOuter);

        if (lgOuter) {
          lgOuter.appendChild(badge);
          console.log('Badge added successfully to lg-outer');
          console.log('Badge element:', badge);
        } else {
          console.warn('.lg-outer not found');
        }
      } else {
        console.warn('Category attribute not found');
      }
    } else {
      console.warn('Invalid index or no gallery items');
    }
  }

  // Method to force resize images in lightGallery viewer
  private resizeImages(): void {
    // Select all images inside lightGallery
    const lgImages = document.querySelectorAll(
      '.lg-item img, .lg-image, .lg-object'
    );

    lgImages.forEach((img) => {
      const element = img as HTMLElement;
      // Apply inline styles to force smaller size
      element.style.cssText = `
        max-width: 100vw !important;
        max-height: 100vh !important;
        width: auto !important;
        height: auto !important;
        object-fit: contain !important;
        // transform: scale(0.75) !important;
      `;
    });

    // Also resize the img-wrap containers
    const lgImgWraps = document.querySelectorAll('.lg-img-wrap');
    lgImgWraps.forEach((wrap) => {
      const element = wrap as HTMLElement;
      element.style.cssText = `
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 100% !important;
        height: 100% !important;
        padding: 60px 20px 20px 20px !important;
        transform: scale(0.75) !important;
      `;
    });

    console.log('Images resized:', lgImages.length);
  }

  // Destroy lightGallery instance to prevent memory leaks
  private destroyLightGallery(): void {
    if (this.lightGalleryInstance) {
      try {
        this.lightGalleryInstance.destroy();
      } catch (error) {
        console.warn('Error destroying lightGallery instance:', error);
      }
      this.lightGalleryInstance = undefined;
    }
  }

  ngOnDestroy(): void {
    this.destroyLightGallery();
  }
}